import clsx from 'clsx';
import { useCallback } from 'react';

import { FormElementText } from '~components/generic/editor/FormElementText';
import useAppSelector from '~hooks/useAppSelector';
import useLabels from '~hooks/useLabels';
import usePermissions from '~hooks/usePermissions';
import { IEntityComponentProps } from '~interfaces/props';
import { MediaGalleryConfig } from '~modules/media-gallery/model/Config';
import MediaLibraryController from '~modules/media-library/controller/MediaLibraryController';
import { getModuleUrlIdFromData } from '~services/location-helpers';
import { selectConfigDefaultMediaLibraryRealm, selectConfigIsMediaLibraryMaintenance } from '~store/selectors';
import MediaLibraryConfigFactory from './model/ConfigFactory';

export default function ModuleMediaLibrary({ data, editing, className, onChange }: IEntityComponentProps) {
  const defaultRealm = useAppSelector(selectConfigDefaultMediaLibraryRealm);

  const isMediaLibraryMaintenance = useAppSelector(selectConfigIsMediaLibraryMaintenance);
  const { hasPassMaintenanceModePermission } = usePermissions();

  // labels
  const {
    hlMediaLibrary,
    msgModuleAttributeMediaLibraryRealmHelp,
    placeholderModuleAttributeMediaLibraryRealm,
    labelModuleAttributeMediaLibraryRealm,
    msgMediaLibraryMaintenance,
  } = useLabels([
    'hlMediaLibrary',
    'msgMediaLibraryMaintenance',
    'msgModuleAttributeMediaLibraryRealmHelp',
    'placeholderModuleAttributeMediaLibraryRealm',
    'labelModuleAttributeMediaLibraryRealm',
  ]);

  // Handler: configuration
  const handleOnChange = useCallback(
    (config: MediaGalleryConfig) => (attr: keyof typeof config) => (value: typeof config[typeof attr]) => {
      const changedConfig = { ...config, [attr]: value };
      onChange('attributes.field_configuration', JSON.stringify(changedConfig));
    },
    [onChange],
  );

  if (isMediaLibraryMaintenance && !hasPassMaintenanceModePermission) {
    return (
      <div className="px-5 py-4">
        <h2>{hlMediaLibrary}</h2>
        <p dangerouslySetInnerHTML={{ __html: msgMediaLibraryMaintenance }}></p>
      </div>
    );
  }

  // Do not render if there is no data!
  if (!data) {
    return null;
  }

  // ClassName generation
  const clsModule = clsx({ ...className.module });
  const clsContainer = clsx({ ...className.container });

  // Get field values
  const fieldConfigValue: string = data.attributes.field_configuration || '';

  // derived values
  const mediaGalleryConfig = MediaLibraryConfigFactory(fieldConfigValue);
  const handleConfigFieldChange = handleOnChange(mediaGalleryConfig);
  const { realm: configRealm } = mediaGalleryConfig;
  const realm = configRealm || defaultRealm;

  return (
    <div className={clsModule} id={getModuleUrlIdFromData(data.id, data.type)}>
      {editing ? (
        <div className={clsContainer}>
          <div className="row c-module__header">
            <div className="col u-margin-bottom-md container px-0">
              <div className="row">
                <h2>{hlMediaLibrary}</h2>
              </div>
              <div className="row u-margin-vertical-sm">
                <div className="col col-12">
                  <FormElementText
                    value={realm}
                    onChange={handleConfigFieldChange('realm')}
                    label={labelModuleAttributeMediaLibraryRealm}
                    placeholder={placeholderModuleAttributeMediaLibraryRealm}
                    helpText={msgModuleAttributeMediaLibraryRealmHelp}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <article className="block relative min-h-screen w-full">
          <MediaLibraryController realm={realm} />
        </article>
      )}
    </div>
  );
}
